import { isInput } from '@n2-common/lib/dom';
import { dotPropsToObjects, flatten, isEmptyObject } from '@n2-common/lib/utils';
import { Formik, FormikConfig, FormikErrors, FormikValues, useFormikContext } from 'formik';
import { createContext, useContext, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { isPromise } from '../../guards';
import React from 'react';

type ValidatorContextType = {
    validationSchema?: Yup.AnySchema;
    validate?: (values: unknown) => void | object | Promise<FormikErrors<unknown>>;
};
export const ValidatorContext = createContext<ValidatorContextType>({});

export default function ValidationFeedbackFormik<V extends FormikValues>(props: FormikConfig<V>) {
    const value: ValidatorContextType = {
        validationSchema: props.validationSchema,
        validate: props.validate as ValidatorContextType['validate'],
    };
    return (
        <ValidatorContext.Provider value={value}>
            <Formik {...props}>{props.children}</Formik>
        </ValidatorContext.Provider>
    );
}

export type ValidValueProps = { onValidValue?: (value: string) => void };

/**
 *
 * @param name Property name in values
 * @returns Tuple with a ref value of the result, and inputhandler.
 * @note This hook is only functional, if used inside a validator context, or else the ref will always return false, and the handler will be undefined.
 */
export function useValidationFeedback<T extends HTMLInputElement | HTMLSelectElement>(
    name: string,
    onValidValue?: ValidValueProps['onValidValue']
): [boolean, React.FormEventHandler<T> | undefined] {
    const [validationResult, setValidationResult] = useState(false);
    const { validationSchema: schema, validate } = useContext(ValidatorContext);
    const { values } = useFormikContext();

    const handleInput = useMemo(() => {
        if (schema) {
            return (e: React.ChangeEvent<T>) => {
                const target = e.target || e.currentTarget;
                const value = getElementValue(target);

                React.startTransition(() => {
                    const current = dotPropsToObjects({ ...flatten(values || {}, '.'), [name]: value });
                    handleValidation(
                        schema.validateAt(name, current, {
                            abortEarly: true,
                        }),
                        setValidationResult,
                        value,
                        onValidValue
                    );
                });
            };
        }
        if (validate) {
            return (e: React.ChangeEvent<T>) => {
                const target = e.target || e.currentTarget;
                const value = getElementValue(target);

                const current = dotPropsToObjects({ ...flatten(values || {}, '.'), [name]: value });
                const mayBePromise = validate(current);
                if (isPromise(mayBePromise)) {
                    handleValidation(mayBePromise, setValidationResult, value);
                } else {
                    setValidationResult(isEmptyObject(mayBePromise));
                }
            };
        }
        return undefined;
    }, [schema, validate, values, name, onValidValue]);

    return [validationResult, handleInput];
}

function getElementValue(target: HTMLInputElement | HTMLSelectElement) {
    return isInput(target) ? (/checkbox/i.test(target.type) ? target.checked : target.value) : target.options[target.selectedIndex].value;
}

function handleValidation(
    validate: Promise<FormikErrors<unknown>>,
    setResult: React.Dispatch<React.SetStateAction<boolean>>,
    value: string | boolean,
    onValidValue?: (value: string) => void
): void {
    validate.then(
        () => {
            setResult(true);
            if(onValidValue){
                 onValidValue(`${value}`);
            }
        },
        () => setResult(false)
    );
}
